import * as THREE from "../../libs/three.js/build/three.module.js";
import {FeatureVisual} from "./FeatureVisual.js";
import {createFeature} from "./Feature.js";
import {GraveReportTool} from "./tools/graveReportTool.js";
import {CutFillVolume} from "../modules/CutFillVolume/CutFillVolume.js";

import {t} from "./i18n/index.js";
// Graverapporten (automatisk udgravning/påfyldning, modules/CutFillVolume) som et varigt objekt under Målinger i Registreringer:
// udgravning (rød) og påfyldning (grøn) fremhæves i 3D på selve punkterne (+ labels) og BLIVER, når dialogen lukkes. Samme mønster som nativeClip.js/
// nativeProfile.js: objektet er en feature uden egen geometri-visning (kun en række i træet), og træets knapper styrer visningen:
//
//   træets øje      ->  3D-visningen vises/skjules
//   træets slet     ->  3D-visningen fjernes
//   dobbeltklik/Vis ->  værten åbner graverapport-dialogen igen med resultatet (open_grave_report_requested + featureId)
//
// En punktsky har højst én graverapport: en ny beregning for samme punktsky erstatter den gamle. Fjernes punktskyen fra scenen,
// fjernes objektet også. Selve resultatet (cut/fill/meta/plan/preview) ligger på visual.userData.graveReport, så rapporten kan hentes igen.

export const GRAVE_REPORT_TOOL_ID = GraveReportTool.id;

class GraveReportVisual extends FeatureVisual {
	constructor(info, centroid){
		super("Point");
		this.editable = false;
		// volumen-labellerne vises fra start i 3D - så står label-knappen i Registreringer også tændt (ellers skulle man klikke to gange)
		this.labelsVisible = true;
		this._centroid = centroid;
		this.userData.graveReport = info;
	}

	// Træets label-knap (og L/Shift+L) skjuler/viser volumen-labels i 3D
	setLabelsVisible(visible){
		super.setLabelsVisible(visible);
		const view = this.userData.graveReportView;
		if(view) view.setLabelsShown(!!visible);
	}

	rawCoordinates(){
		return [[this._centroid.x, this._centroid.y, this._centroid.z]];
	}

	toCoordinates(){
		return this.rawCoordinates()[0];
	}
}

function pointcloudKey(pointcloud){
	return pointcloud.name;
}

// Feature-id'et for en punktskys graverapport (null hvis den ikke har en).
export function findGraveReport(dm, pointcloudId){
	for(const [featureId, visual] of dm.visuals){
		const info = visual.userData.graveReport;
		if(info && info.pointcloudId === pointcloudId) return featureId;
	}
	return null;
}

// {pointcloudId, name, result} bag en feature (null for alle andre registreringer).
export function getGraveReport(dm, featureId){
	const visual = dm.getVisual(featureId);
	return visual && visual.userData.graveReport ? visual.userData.graveReport : null;
}

// Registrerer (og viser i 3D) en graverapport for en indlæst punktsky. Erstatter en eksisterende for samme punktsky.
// info: {name (punktskyens visningsnavn), result (resultatet fra Potree.computeCutFillVolume)}. Returnerer feature-id'et.
export function registerGraveReport(dm, pointcloud, {name, result}){
	if(!pointcloud || !result || !result.cut) return null;

	const pointcloudId = pointcloudKey(pointcloud);
	const existing = findGraveReport(dm, pointcloudId);
	if(existing) dm.removeFeature(existing);

	// midtpunktet af udgravningen (ellers påfyldningen) i verdenskoordinater - kun til træets geometri/zoom
	const region = result.cut.volume_m3 > 0 ? result.cut : result.fill;
	const local = new THREE.Vector3(...(region && region.centroid ? region.centroid : [0, 0, 0]));
	pointcloud.updateMatrixWorld(true);
	const world = pointcloud.localToWorld(local.clone());

	const info = {pointcloudId, name: name || pointcloudId, result};
	const visual = new GraveReportVisual(info, world);
	dm.scene.add(visual);

	// 3D-visning som den oprindelige "Vis i 3D": udgravningen (rød) og påfyldningen (grøn) fremhæves på selve punkterne, med
	// labels. Fremhævningen er polygon-clip-volumener markeret som interne (ingen "Clip Polygon"-rækker i Registreringer).
	const view = new CutFillVolume(dm.viewer, pointcloud, result);
	visual.userData.graveReportView = view;

	const feature = createFeature({
		geometryType: "Point",
		coordinates: visual.toCoordinates(),
		category: GraveReportTool.category,
		tool: GRAVE_REPORT_TOOL_ID,
		featureType: GraveReportTool.featureType,
		name: t("Graverapport {0}", info.name),
		attributes: {pointcloudId},
	});
	visual.setInfo(feature.properties.name, feature.properties.description);

	// Træets øje styrer 3D-visningen (markering + labels), uden at flytte kameraet.
	Object.defineProperty(visual, "visible", {
		configurable: true,
		get: () => view.visible !== false,
		set: (value) => view.setShown(value),
	});

	dm._wireFeatureLifecycle(feature, visual);
	// et rapport-midtpunkt er ikke et snap-mål
	dm.removeFeatureSnapTargets(feature.id);

	let released = false;
	const release = () => {
		if(released) return false;
		released = true;
		dm.viewer.removeEventListener("update", onUpdate);
		dm.featureManager.removeEventListener("feature_removed", onFeatureRemoved);
		return true;
	};
	const onFeatureRemoved = (e) => {
		if(!e.feature || e.feature.id !== feature.id) return;
		if(!release()) return;
		view.dispose();
	};
	// punktskyen er fjernet fra scenen (fx ved skift af punktsky): objektet fjernes med den
	const onUpdate = () => {
		if(dm.viewer.scene.pointclouds.indexOf(pointcloud) === -1 && release()){
			view.dispose();
			if(dm.getVisual(feature.id)) dm.removeFeature(feature.id);
		}
	};
	dm.viewer.addEventListener("update", onUpdate);
	dm.featureManager.addEventListener("feature_removed", onFeatureRemoved);

	dm.dispatchEvent({type: "feature_created", feature, visual});
	return feature.id;
}
